import { map, sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import customChannelRecipientResource from 'src/actions/channels/customChannelRecipientResource';
import { toastError } from 'src/actions/toast';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import useCustomChannel from './useCustomChannel';
import useResource, { getConsolidatedState, someFailed, STATUS_FAILED } from 'src/presentation/hooks/useResource';

type UseRecipientCollectionsType = { customChannelId: string };

const useRecipientCollections = ({ customChannelId }: UseRecipientCollectionsType) => {
  const dispatch = useDispatch();

  const { customChannels, loadingState: customChannelLoadingState, usersMap } = useCustomChannel();

  const {
    loadingState: recipientCollectionsLoadingState,
    values: collectionsValues,
    errors: recipientIdsErrors,
  } = useResource(customChannelRecipientResource, {
    fetch: true,
    params: { customChannelId },
  });

  const loadingStates = [recipientCollectionsLoadingState, customChannelLoadingState];
  const fetchError = someFailed(...loadingStates);
  const loadingState = fetchError ? STATUS_FAILED : getConsolidatedState(loadingStates);

  const recipientCollectionsValues = useMemo(
    () =>
      map(
        sortBy(collectionsValues, item => item.createdAt),
        value => ({
          ...value,
          updatedAtString: toDateTimeString(value.updatedAt),
          updatedByString: usersMap[value?.updatedBy?.userId]?.name,
        })
      ),
    [collectionsValues, usersMap]
  );

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [fetchError, dispatch]);

  return { customChannels, recipientCollections: recipientCollectionsValues, loadingState, recipientIdsErrors };
};

export default useRecipientCollections;
